// src/pages/campaigns/CampaignForm.jsx
import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    Campaign as CampaignIcon,
    BarChart as KpiIcon,
    Layers as LayersIcon,
    AutoAwesome as MagicIcon,
    AttachMoney as MoneyIcon,
    Save as SaveIcon,
    Group as TeamIcon,
    Psychology as PsychologyIcon
} from '@mui/icons-material';
import {
    alpha,
    Box,
    Button,
    useTheme
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Import custom components
import { Card, MagicLoader } from '../../design-system';

// Import form standardized components
import { FormStepperEnhanced, FormStep } from '../../components/shared/forms';
import { FormPageHeader } from '../../components/ui/FormFields';

// Import form sections
import CampaignBasicInfoForm from './forms/CampaignBasicInfoForm';
import CampaignBudgetForm from './forms/CampaignBudgetForm';
import CampaignContentPlanForm from './forms/CampaignContentPlanForm';
import CampaignKPIsForm from './forms/CampaignKPIsForm';
import CampaignTargetAudienceForm from './forms/CampaignTargetAudienceForm';
import CampaignTeamForm from './forms/CampaignTeamForm';
import MagicalElementsForm from '../../components/ui/MagicalElementsForm';

// Import hooks and stores
import useCampaignStore from '../../stores/campaignStore';
import useClientStore from '../../stores/clientStore';
import useBrandStore from '../../stores/brandStore';
import useNotificationStore from '../../stores/notificationStore';

// Initial campaign data
const initialCampaignData = {
  // Basic Information
  name: '',
  description: '',
  brief: '',
  clientId: '',
  brandId: '',
  type: 'awareness',
  status: 'draft',
  dates: {
    plannedStartDate: '',
    plannedEndDate: '',
  },
  platforms: {
    facebook: false,
    instagram: false,
    twitter: false,
    linkedin: false,
    youtube: false,
    tiktok: false,
    snapchat: false,
    googleAds: false,
    other: []
  },
  
  // Target audience
  targetAudience: {
    demographics: {
      ageRange: {
        min: 18,
        max: 65
      },
      gender: 'all',
      locations: [], // This is required but starts empty
      languages: []
    },
    psychographics: {
      interests: [],
      behaviors: [],
      values: [],
      lifestyleFactors: []
    },
    customSegments: []
  },
  
  // Budget
  budget: {
    total: 0,
    currency: 'USD',
    allocation: {},
    spent: 0,
    remaining: 0,
    transactions: []
  },
  
  // Content plan
  contentPlan: {
    frequency: 'weekly',
    customFrequency: 0,
    totalPlannedPosts: 0,
    contentTypes: [],
    contentPillars: [],
    contentThemes: [],
    hashtags: []
  },
  
  // KPIs
  kpis: [],
  
  // Team
  team: [],
  
  // Magical elements
  magicalElements: {
    transformationNarrative: '',
    enchantmentLevel: 'moderate',
    specialFeatures: []
  }
};

// Campaign form component
const CampaignForm = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Parse query params if creating
  const queryParams = new URLSearchParams(location.search);
  const clientIdParam = queryParams.get('clientId');
  const brandIdParam = queryParams.get('brandId');
  
  // Is this an edit form or create form
  const isEditMode = Boolean(id);
  
  // Store hooks
  const { 
    currentCampaign,
    isLoading, 
    error, 
    fetchCampaign,
    createCampaign,
    updateCampaign,
    clearError,
    clearCurrentCampaign,
  } = useCampaignStore();
  
  const { 
    clients: clientsList,
    fetchClients 
  } = useClientStore();
  
  const { 
    fetchBrands,
    fetchClientBrands 
  } = useBrandStore();
  
  const { showSuccess, showError } = useNotificationStore();
  
  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [campaignData, setCampaignData] = useState({...initialCampaignData});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(false);
  
  // Note: We'll handle validation error clearing directly through the validationErrors state
  
  // Load campaign data for edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchCampaign(id);
    } else {
      // If creating with client/brand ID, set it in the form
      if (clientIdParam) {
        setCampaignData(prev => ({
          ...prev,
          clientId: clientIdParam
        }));
        
        // If we have a client ID, load brands for that client
        if (clientIdParam) {
          loadBrandsForClient(clientIdParam);
        }
      }
      
      if (brandIdParam) {
        setCampaignData(prev => ({
          ...prev,
          brandId: brandIdParam
        }));
      }
    }
    
    // Load client options
    const loadClients = async () => {
      setClientsLoading(true);
      try {
        await fetchClients({ limit: 100 });
        
        // Use clients from store
        const clientsWithValidIds = (clientsList || []).map(client => {
          // Ensure client has a valid ID
          const clientId = client._id || client.id || `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          
          return {
            ...client,
            _id: String(clientId),
            id: String(clientId)
          };
        });
        
        // Set the enhanced clients in state
        setClients(clientsWithValidIds);
      } catch (err) {
        console.error('Error loading clients:', err);
        // Error is already handled by the store
        setClients([]); // Set empty array on error
      } finally {
        setClientsLoading(false);
      }
    };
    
    loadClients();
    
    // Cleanup
    return () => {
      clearError();
      clearCurrentCampaign();
    };
  }, [fetchCampaign, id, isEditMode, clientIdParam, brandIdParam, clearError, clearCurrentCampaign, fetchClients]);
  
  // Load brands for a client
  const loadBrandsForClient = async (clientId) => {
    if (!clientId) {
      setBrands([]);
      return;
    }
    
    // Extract string ID from clientId parameter
    let actualClientId;
    if (typeof clientId === 'string') {
      actualClientId = clientId;
    } else if (typeof clientId === 'object' && clientId !== null) {
      actualClientId = clientId._id || clientId.id;
    } else {
      setBrands([]);
      return;
    }
    
    // Ensure we have a valid string ID
    if (!actualClientId || typeof actualClientId !== 'string') {
      setBrands([]);
      return;
    }
    
    setBrandsLoading(true);
    try {
      const response = await fetchClientBrands(actualClientId);
      
      // Get brands directly from the response instead of relying on store
      const fetchedBrands = response?.brands || [];
      const brandsWithValidIds = fetchedBrands.map(brand => {
        // Ensure brand has a valid ID
        const brandId = brand._id || brand.id || `brand-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        return {
          ...brand,
          _id: String(brandId),
          id: String(brandId)
        };
      });
      
      // Set the enhanced brands in state
      setBrands(brandsWithValidIds);
    } catch (err) {
      console.error('Error loading brands:', err);
      // Error is already handled by the store
      setBrands([]); // Set empty array on error
    } finally {
      setBrandsLoading(false);
    }
  };
  
  // Update form data when currentCampaign changes
  useEffect(() => {
    if (currentCampaign && isEditMode) {
      setCampaignData(currentCampaign);
      
      // Load brands for the campaign's client
      if (currentCampaign.clientId) {
        loadBrandsForClient(
          typeof currentCampaign.clientId === 'object' 
            ? currentCampaign.clientId._id 
            : currentCampaign.clientId
        );
      }
    }
  }, [currentCampaign, isEditMode]);

  // Track previous clientId to detect actual changes
  const prevClientIdRef = useRef(campaignData.clientId);
  
  // Handle client change - load brands for the selected client
  useEffect(() => {
    // Skip if clientId hasn't actually changed
    if (prevClientIdRef.current === campaignData.clientId) {
      return;
    }
    
    // Update ref for next comparison
    prevClientIdRef.current = campaignData.clientId;
    
    // When client ID changes
    if (campaignData.clientId) {
      // Enhanced logging for debugging purposes
      console.log('Client ID changed:', campaignData.clientId, 'Previous clientIdParam:', clientIdParam);
      console.log('Current campaignData state:', campaignData);
      
      // Load brands for the selected client
      loadBrandsForClient(campaignData.clientId);
      
      // Always reset brandId when changing clients unless we're in editMode
      // This ensures the brand dropdown is reset when a new client is selected
      if (!isEditMode && !brandIdParam) {
        console.log('Resetting brandId due to client change');
        setCampaignData(prev => ({
          ...prev,
          brandId: '',
        }));
      }
    } else {
      // If clientId is cleared, also reset brandId
      console.log('ClientId is empty, resetting brands list');
      setBrands([]);
      if (!isEditMode) {
        setCampaignData(prev => ({
          ...prev,
          brandId: '',
        }));
      }
    }
  }, [campaignData.clientId, isEditMode, clientIdParam, brandIdParam]);
  
  // Handle step change
  const handleStepChange = (newStep) => {
    setActiveStep(newStep);
    window.scrollTo(0, 0);
  };
  
  // Handle form field changes
  const handleChange = (nameOrValue, valueOrName) => {
    // Direct debug - what's coming in from client selection?
    console.log('🔬 RAW FORM CHANGE:', { nameOrValue, valueOrName, types: { nameType: typeof nameOrValue, valueType: typeof valueOrName } });
    
    // Check if this change resolves any validation errors and update the stepper error indicator
    const fieldName = typeof nameOrValue === 'string' ? nameOrValue : '';
    const fieldValue = typeof valueOrName !== 'undefined' ? valueOrName : '';
    
    // Clear validation error for this field if it was previously invalid but now has a value
    if (validationErrors[fieldName] && fieldValue) {
      setValidationErrors(prev => {
        const updated = {...prev};
        delete updated[fieldName];
        
        // If no validation errors remain for this step, we'll let the validateStep function
        // handle clearing the validation error state in the stepper automatically
        
        return updated;
      });
    }
    
    // DIRECT CLIENT ID HANDLING
    if (nameOrValue === 'clientId') {
      // Use fallback system to find a usable client ID
      let clientId;
      
      // If we have an explicit value, use it
      if (valueOrName && valueOrName !== 'undefined') {
        clientId = valueOrName;
      } 
      // If we got an object instead, extract ID
      else if (typeof valueOrName === 'object' && valueOrName !== null) {
        clientId = valueOrName._id || valueOrName.id || `synthetic-${Date.now()}`;
      }
      // If the value is synthetic (from MagicSelect normalization), use it
      else if (typeof valueOrName === 'string' && valueOrName.startsWith('option-')) {
        clientId = valueOrName;
      }
      // No valid ID - use empty string
      else {
        clientId = '';
      }
      
      console.log('👑 CLIENT SELECTED:', { 
        rawValue: valueOrName,
        processedId: clientId,
        empty: !clientId,
        typeof: typeof clientId,
      });
      
      // Only proceed with update if we have a client ID
      if (clientId) {
        // SET CLIENT ID
        setCampaignData(prevData => {
          const updatedData = {
            ...prevData,
            clientId,
            // Reset brandId when client changes
            brandId: ''
          };
          
          console.log('⚡ UPDATING CAMPAIGN DATA:', updatedData);
          return updatedData;
        });
        
        // Mark field as touched
        setTouchedFields(prev => ({
          ...prev,
          [activeStep]: {
            ...(prev[activeStep] || {}),
            clientId: true
          }
        }));
        
        // Load brands for this client
        loadBrandsForClient(clientId);
      } else {
        // Clear client and brand selection
        setCampaignData(prevData => ({
          ...prevData,
          clientId: '',
          brandId: ''
        }));
      }
      
      return;
    }
    
    // DIRECT BRAND ID HANDLING
    if (nameOrValue === 'brandId') {
      // Use fallback system to find a usable brand ID
      let brandId;
      
      // If we have an explicit value, use it
      if (valueOrName && valueOrName !== 'undefined') {
        brandId = valueOrName;
      } 
      // If we got an object instead, extract ID
      else if (typeof valueOrName === 'object' && valueOrName !== null) {
        brandId = valueOrName._id || valueOrName.id || `synthetic-${Date.now()}`;
      }
      // If the value is synthetic (from MagicSelect normalization), use it
      else if (typeof valueOrName === 'string' && valueOrName.startsWith('option-')) {
        brandId = valueOrName;
      }
      // No valid ID - use empty string
      else {
        brandId = '';
      }
      
      console.log('👑 BRAND SELECTED:', { 
        rawValue: valueOrName,
        processedId: brandId,
        empty: !brandId,
        typeof: typeof brandId,
      });
      
      // Only proceed with update if we have a brand ID
      if (brandId) {
        // SET BRAND ID
        setCampaignData(prevData => {
          const updatedData = {
            ...prevData,
            brandId
          };
          
          console.log('⚡ UPDATING CAMPAIGN DATA:', updatedData);
          return updatedData;
        });
        
        // Mark field as touched
        setTouchedFields(prev => ({
          ...prev,
          [activeStep]: {
            ...(prev[activeStep] || {}),
            brandId: true
          }
        }));
      } else {
        // Clear brand selection
        setCampaignData(prevData => ({
          ...prevData,
          brandId: ''
        }));
      }
      
      return;
    }
    
    // Enhanced logic to detect parameter order
    let detectedFieldName, detectedFieldValue;
    
    // Case 1: Event-based onChange (e) - extremely rare but handled for completeness
    if (nameOrValue && nameOrValue.target && nameOrValue.target.name) {
      detectedFieldName = nameOrValue.target.name;
      detectedFieldValue = nameOrValue.target.value;
      console.log('Event-based onChange detected', { detectedFieldName, detectedFieldValue });
    }
    // Case 2: (name, value) parameter order - from most form components
    else if (typeof nameOrValue === 'string' && 
             (nameOrValue.includes('Id') || 
              nameOrValue === 'name' || 
              nameOrValue === 'description' || 
              nameOrValue === 'status' || 
              nameOrValue === 'type' ||
              nameOrValue === 'brief')) {
      detectedFieldName = nameOrValue;
      detectedFieldValue = valueOrName;
      console.log('Name-value parameter order detected', { detectedFieldName, detectedFieldValue });
    }
    // Case 3: (value, name) parameter order - from MagicSelect
    else if (typeof valueOrName === 'string' && 
             (valueOrName.includes('Id') || 
              valueOrName === 'name' || 
              valueOrName === 'type' || 
              valueOrName === 'status')) {
      detectedFieldName = valueOrName;
      detectedFieldValue = nameOrValue;
      console.log('Value-name parameter order detected', { detectedFieldName, detectedFieldValue });
    }
    // Case 4: Fallback - just use parameters as-is with name-value convention
    else {
      detectedFieldName = nameOrValue;
      detectedFieldValue = valueOrName;
      console.log('Default parameter handling', { detectedFieldName, detectedFieldValue });
    }
    
    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [activeStep]: {
        ...(prev[activeStep] || {}),
        [detectedFieldName]: true
      }
    }));
    
    // Update form state with the extracted field name and value
    setCampaignData((prevData) => {
      const newData = {
        ...prevData,
        [detectedFieldName]: detectedFieldValue,
      };
      console.log(`Updated field ${detectedFieldName} to:`, detectedFieldValue);
      console.log('New campaign data:', newData);
      
      // Clear validation error for this field if it was previously invalid but now has a value
      if (validationErrors[detectedFieldName] && detectedFieldValue) {
        setValidationErrors(prev => {
          const updated = {...prev};
          delete updated[detectedFieldName];
          
          // If no validation errors remain, the next validation check will clear the error states automatically
          
          return updated;
        });
      }
      
      return newData;
    });
  };
  
  // Handle nested field changes (for objects)
  const handleNestedChange = (parent, name, value) => {
    // Update campaign data
    setCampaignData((prevData) => ({
      ...prevData,
      [parent]: {
        ...prevData[parent],
        [name]: value,
      },
    }));
    
    // Mark nested field as touched
    setTouchedFields(prev => ({
      ...prev,
      [activeStep]: {
        ...(prev[activeStep] || {}),
        [`${parent}.${name}`]: true
      }
    }));
    
    // Clear validation error for this nested field if it has a value now
    const nestedFieldKey = `${parent}.${name}`;
    if (validationErrors[nestedFieldKey] && value) {
      setValidationErrors(prev => {
        const updated = {...prev};
        delete updated[nestedFieldKey];
        
        // If no validation errors remain, the next validation check will clear the error states automatically
        
        return updated;
      });
    }
  };
  
  // Handle deeply nested field changes
  const handleDeepNestedChange = (parent, child, name, value) => {
    // Update campaign data
    setCampaignData((prevData) => ({
      ...prevData,
      [parent]: {
        ...prevData[parent],
        [child]: {
          ...prevData[parent]?.[child],
          [name]: value,
        },
      },
    }));
    
    // Mark deeply nested field as touched
    setTouchedFields(prev => ({
      ...prev,
      [activeStep]: {
        ...(prev[activeStep] || {}),
        [`${parent}.${child}.${name}`]: true
      }
    }));
    
    // Clear validation error for this deeply nested field if it has a value now
    const deepNestedFieldKey = `${parent}.${child}.${name}`;
    if (validationErrors[deepNestedFieldKey] && value) {
      setValidationErrors(prev => {
        const updated = {...prev};
        delete updated[deepNestedFieldKey];
        
        // If no validation errors remain, the next validation check will clear the error states automatically
        
        return updated;
      });
    }
  };
  
  // State for validation errors and touched fields
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Step validation function
  const validateStep = async (stepIndex) => {
    // Reset validation errors for this step
    setValidationErrors({});
    const errors = {};
    let isValid = true;

    // Create a temporary object to track fields that need to be marked as touched
    // if validation fails
    const fieldsToTouch = {};

    // Validate based on step index
    switch (stepIndex) {
      case 0: // Basic Information
        // Validate required fields
        if (!campaignData.name || campaignData.name.trim() === '') {
          errors.name = 'Campaign name is required';
          fieldsToTouch.name = true;
          isValid = false;
        }
        
        if (!campaignData.clientId) {
          errors.clientId = 'Client is required';
          fieldsToTouch.clientId = true;
          isValid = false;
        }
        
        if (!campaignData.brandId) {
          errors.brandId = 'Brand is required';
          fieldsToTouch.brandId = true;
          isValid = false;
        }

        if (!campaignData.dates || !campaignData.dates.plannedStartDate) {
          errors['dates.plannedStartDate'] = 'Start date is required';
          fieldsToTouch['dates.plannedStartDate'] = true;
          isValid = false;
        }

        if (!campaignData.dates || !campaignData.dates.plannedEndDate) {
          errors['dates.plannedEndDate'] = 'End date is required';
          fieldsToTouch['dates.plannedEndDate'] = true;
          isValid = false;
        }
        break;

      case 1: // Target Audience
        // Check if at least one location is defined
        if (!campaignData.targetAudience?.demographics?.locations || 
            campaignData.targetAudience.demographics.locations.length === 0) {
          errors['targetAudience.demographics.locations'] = 'At least one location is required';
          fieldsToTouch['targetAudience.demographics.locations'] = true;
          isValid = false;
        }
        break;

      case 2: // Budget
        // Validate budget total
        if (!campaignData.budget?.total || campaignData.budget.total <= 0) {
          errors['budget.total'] = 'Total budget must be greater than zero';
          fieldsToTouch['budget.total'] = true;
          isValid = false;
        }

        // Check if allocations sum to 100%
        const totalAllocation = Object.values(campaignData.budget?.allocation || {})
          .reduce((sum, allocation) => sum + (allocation.percentage || 0), 0);
          
        if (totalAllocation > 0 && totalAllocation < 100) {
          errors['budget.allocation'] = 'Budget allocation should total 100%';
          fieldsToTouch['budget.allocation'] = true;
          isValid = false;
        }
        break;

      case 3: // Content Plan
        // Validate content types
        if (!campaignData.contentPlan?.contentTypes || 
            campaignData.contentPlan.contentTypes.length === 0) {
          errors['contentPlan.contentTypes'] = 'At least one content type is required';
          fieldsToTouch['contentPlan.contentTypes'] = true;
          isValid = false;
        }
        
        // Validate total planned posts
        if (!campaignData.contentPlan?.totalPlannedPosts || 
            campaignData.contentPlan.totalPlannedPosts <= 0) {
          errors['contentPlan.totalPlannedPosts'] = 'Total planned posts must be greater than zero';
          fieldsToTouch['contentPlan.totalPlannedPosts'] = true;
          isValid = false;
        }
        break;

      case 4: // KPIs
        // Check if at least one KPI is defined
        if (!campaignData.kpis || campaignData.kpis.length === 0) {
          errors.kpis = 'At least one KPI is required';
          fieldsToTouch.kpis = true;
          isValid = false;
        }
        break;

      case 5: // Team
        // Check if at least one team member with manager role exists
        const hasManager = campaignData.team?.some(member => member.role === 'manager');
        if (!hasManager) {
          errors.team = 'At least one team member with Manager role is required';
          fieldsToTouch.team = true;
          isValid = false;
        }
        break;

      case 6: // Magical Elements (optional)
        // No validation needed for this optional step
        break;

      default:
        break;
    }

    // Update validation errors state
    setValidationErrors(errors);
    
    // Only mark fields as touched if validation fails
    if (!isValid) {
      // Update touched fields state with the fields that failed validation
      // Use Promise to ensure state update completes before returning
      await new Promise(resolve => {
        setTouchedFields(prev => {
          const result = {
            ...prev,
            [stepIndex]: {
              ...(prev[stepIndex] || {}),
              ...fieldsToTouch
            }
          };
          // Resolve after state update is applied
          setTimeout(resolve, 10);
          return result;
        });
      });
      
      // Log validation results
      console.log(`Validation failed for step ${stepIndex}:`, errors);
      console.log('Fields to mark as touched:', fieldsToTouch);
    }
    
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      if (isEditMode) {
        // Update existing campaign
        await updateCampaign(id, campaignData);
        showSuccess(`Campaign "${campaignData.name}" updated successfully`);
        navigate(`/campaigns/${id}`);
      } else {
        // Create new campaign
        const newCampaign = await createCampaign(campaignData);
        showSuccess(`Campaign "${campaignData.name}" created successfully`);
        navigate(`/campaigns/${newCampaign._id}`);
      }
    } catch (err) {
      showError(err.message || 'Failed to save campaign');
      setIsSubmitting(false);
    }
  };
  
  // Loading state
  if ((isLoading && isEditMode && !currentCampaign) || clientsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoader variant="mystical" size="large" message={isEditMode ? "Loading campaign details..." : "Preparing campaign form..."} />
      </Box>
    );
  }
  
  // Error state
  if (error && isEditMode) {
    return (
      <Box sx={{ py: 3 }}>
        <Card
          elevation={0}
          sx={{ 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/campaigns')}
          >
            Back to Campaigns
          </Button>
        </Card>
      </Box>
    );
  }


  // This declaration was moved up in the component

  // Function to pass errors to form components
  const getErrorsForStep = (stepIndex) => {
    switch (stepIndex) {
      case 0: // Basic Information
        return {
          name: validationErrors.name,
          clientId: validationErrors.clientId,
          brandId: validationErrors.brandId,
          'dates.plannedStartDate': validationErrors['dates.plannedStartDate'],
          'dates.plannedEndDate': validationErrors['dates.plannedEndDate']
        };
      case 1: // Target Audience
        return {
          'targetAudience.demographics.locations': validationErrors['targetAudience.demographics.locations']
        };
      case 2: // Budget
        return {
          'budget.total': validationErrors['budget.total'],
          'budget.allocation': validationErrors['budget.allocation']
        };
      case 3: // Content Plan
        return {
          'contentPlan.contentTypes': validationErrors['contentPlan.contentTypes'],
          'contentPlan.totalPlannedPosts': validationErrors['contentPlan.totalPlannedPosts']
        };
      case 4: // KPIs
        return {
          kpis: validationErrors.kpis
        };
      case 5: // Team
        return {
          team: validationErrors.team
        };
      case 6: // Magical Elements
        return {};
      default:
        return {};
    }
  };
  
  // Function to get touched fields for a step
  const getTouchedForStep = (stepIndex) => {
    // Return the current touched state for this step
    return touchedFields[stepIndex] || {};
  };

  // Define steps for FormStepperEnhanced 
  const formSteps = [
    {
      label: 'Basic Information',
      icon: <CampaignIcon />,
      optional: false,
      description: 'Enter core campaign details',
      component: (
        <FormStep
          label="Basic Information"
          description="Enter core campaign details"
          icon={<CampaignIcon />}
        >
          <CampaignBasicInfoForm
            campaignData={campaignData}
            onChange={handleChange}
            onNestedChange={handleNestedChange}
            clients={clients}
            brands={brands}
            isEditMode={isEditMode}
            brandsLoading={brandsLoading}
            errors={getErrorsForStep(0)}
            touched={getTouchedForStep(0)}
          />
        </FormStep>
      )
    },
    {
      label: 'Target Audience',
      icon: <PsychologyIcon />,
      optional: false,
      description: 'Define who you want to reach',
      component: (
        <FormStep
          label="Target Audience"
          description="Define who you want to reach"
          icon={<PsychologyIcon />}
        >
          <CampaignTargetAudienceForm
            campaignData={campaignData}
            onChange={handleChange}
            onNestedChange={handleNestedChange}
            onDeepNestedChange={handleDeepNestedChange}
            errors={getErrorsForStep(1)}
            touched={getTouchedForStep(1)}
          />
        </FormStep>
      )
    },
    {
      label: 'Budget',
      icon: <MoneyIcon />,
      optional: false,
      description: 'Set budget and financial details',
      component: (
        <FormStep
          label="Budget"
          description="Set budget and financial details"
          icon={<MoneyIcon />}
        >
          <CampaignBudgetForm
            campaignData={campaignData}
            onChange={handleChange}
            onNestedChange={handleNestedChange}
            errors={getErrorsForStep(2)}
            touched={getTouchedForStep(2)}
          />
        </FormStep>
      )
    },
    {
      label: 'Content Plan',
      icon: <LayersIcon />,
      optional: false,
      description: 'Plan your content strategy',
      component: (
        <FormStep
          label="Content Plan"
          description="Plan your content strategy"
          icon={<LayersIcon />}
        >
          <CampaignContentPlanForm
            campaignData={campaignData}
            onChange={handleChange}
            onNestedChange={handleNestedChange}
            errors={getErrorsForStep(3)}
            touched={getTouchedForStep(3)}
          />
        </FormStep>
      )
    },
    {
      label: 'KPIs',
      icon: <KpiIcon />,
      optional: false,
      description: 'Define success metrics',
      component: (
        <FormStep
          label="KPIs"
          description="Define success metrics"
          icon={<KpiIcon />}
        >
          <CampaignKPIsForm
            campaignData={campaignData}
            onChange={handleChange}
            errors={getErrorsForStep(4)}
            touched={getTouchedForStep(4)}
          />
        </FormStep>
      )
    },
    {
      label: 'Team',
      icon: <TeamIcon />,
      optional: false,
      description: 'Assign team members',
      component: (
        <FormStep
          label="Team"
          description="Assign team members"
          icon={<TeamIcon />}
        >
          <CampaignTeamForm
            campaignData={campaignData}
            onChange={handleChange}
            errors={getErrorsForStep(5)}
            touched={getTouchedForStep(5)}
          />
        </FormStep>
      )
    },
    {
      label: 'Magical Elements',
      icon: <MagicIcon />,
      optional: true,
      description: 'Add magical elements to your campaign',
      component: (
        <FormStep
          label="Magical Elements"
          description="Add magical elements to your campaign"
          icon={<MagicIcon />}
        >
          <MagicalElementsForm
            data={campaignData}
            onChange={handleChange}
            onNestedChange={handleNestedChange}
            errors={getErrorsForStep(6)}
            touched={getTouchedForStep(6)}
          />
        </FormStep>
      )
    }
  ];

  // Define breadcrumbs for FormPageHeader
  const breadcrumbs = [
    { label: 'Campaigns', path: '/campaigns' },
    { label: isEditMode ? `Edit ${currentCampaign?.name || 'Campaign'}` : 'Create Campaign' }
  ];
  

  return (
    <Box sx={{ py: 3 }}>
      {/* Page Header with Breadcrumbs */}
      <FormPageHeader
        title={isEditMode ? `Edit ${currentCampaign?.name || 'Campaign'}` : 'Create New Campaign'}
        subtitle={isEditMode 
          ? 'Update your campaign details, target audience, budget, and planning' 
          : 'Set up a new marketing campaign with detailed targeting and planning'}
        breadcrumbs={breadcrumbs}
        entityType="Campaign"
        isEditMode={isEditMode}
      />
      
      {/* Enhanced Form Stepper */}
      <FormStepperEnhanced
        steps={formSteps}
        activeStep={activeStep}
        onStepChange={handleStepChange}
        onSubmit={handleSubmit}
        validateStep={validateStep}
        submitButtonText={isEditMode ? 'Save Changes' : 'Create Campaign'}
        submitButtonIcon={<SaveIcon />}
        submitting={isSubmitting}
        cancelLink="/campaigns"
        magical={true}
        nextButtonText="Next"
        backButtonText="Back"
        formState={campaignData}
        setFormState={setCampaignData}
        errors={validationErrors}
      />
    </Box>
  );
};

export default CampaignForm;